import React, { useState } from "react";
import { Star, Quote, ExternalLink } from "lucide-react";
import { Reveal, RevealStagger, StaggerItem } from "@/components/Reveal";
import { TESTIMONIALS, DOCTOR } from "@/data/content";

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

const ReviewCard = ({ t, i }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = t.text.length > 420;
  return (
    <div data-testid={`testimonial-${i}`} className="glass-card rounded-2xl p-8 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <Quote className="w-8 h-8 text-brand/30" />
        <GoogleG />
      </div>

      <div className="flex mb-3">
        {[...Array(t.rating)].map((_, s) => (
          <Star key={`rating-star-${s}`} className="w-4 h-4 text-amber-400" fill="#fbbf24" />
        ))}
      </div>

      <p className={`text-slate-600 leading-relaxed flex-1 ${!expanded && isLong ? "line-clamp-6" : ""}`}>
        {"\u201C"}{t.text}{"\u201D"}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`testimonial-toggle-${i}`}
          className="mt-2 self-start text-sm font-semibold text-brand hover:text-brand-hover"
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}

      <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
        <div>
          <div className="font-semibold text-[#0b2c4d] text-sm">{t.name}</div>
          <div className="text-slate-400 text-xs">{t.meta}</div>
        </div>
        {t.time && <div className="text-slate-400 text-xs">{t.time}</div>}
      </div>
    </div>
  );
};

const Testimonials = () => {
  return (
    <section data-testid="testimonials-section" className="relative bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand flex items-center gap-2">
              <GoogleG /> Reviews from Google
            </p>
            <h2 className="font-heading font-extrabold text-[#0b2c4d] text-4xl md:text-5xl tracking-tight mt-3 leading-tight">
              Trusted by patients across Hyderabad
            </h2>
          </div>
          <a
            href={DOCTOR.reviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-link"
            className="inline-flex items-center gap-2 glass-card rounded-full px-5 py-3 text-sm text-[#0b2c4d] hover:text-brand hover:shadow-lg transition-all shrink-0"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={`link-star-${i}`} className="w-4 h-4 text-amber-400" fill="#fbbf24" />
              ))}
            </div>
            Read all reviews on Google
            <ExternalLink className="w-4 h-4" />
          </a>
        </Reveal>

        <RevealStagger className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <StaggerItem key={t.name} className="h-full">
              <ReviewCard t={t} i={i} />
            </StaggerItem>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
};

export default Testimonials;
